import type {ImportState} from '@gaia/types';
import {type DragEvent, useCallback, useEffect, useRef} from 'react';

interface ImportSurfaceProps {
    interaction: InteractionState;
    onInteractionChange: SetState<InteractionState>;
    onSidebarChange: SetState<SidebarState>;
    setImportState: SetState<ImportState>;
    children: ReactNode;
}

import classes from './Map.module.css';

export function ImportSurface({
    interaction,
    onInteractionChange,
    onSidebarChange,
    setImportState,
    children,
}: ImportSurfaceProps) {
    const interactionSubtypeRef = useRef(interaction.subtype);

    useEffect(() => {
        interactionSubtypeRef.current = interaction.subtype;
    });

    const onDragOver = useCallback(
        (event: DragEvent<HTMLElement>) => {
            event.preventDefault();

            if (interactionSubtypeRef.current === 'draggingFile') return;

            // Only show when files and not DOM elements are dragged
            if (!event.dataTransfer.types.includes('Files')) return;

            onInteractionChange({
                type: 'normal',
                subtype: 'draggingFile',
            });
        },
        [onInteractionChange]
    );

    const onDragLeave = useCallback(
        (event: DragEvent<HTMLElement>) => {
            event.preventDefault();
            onInteractionChange({type: 'normal', subtype: 'normal'});
        },
        [onInteractionChange]
    );

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();

            const {files}: {files: FileList} = event.dataTransfer;

            if (files.length === 0) return;

            const fileNames = Array.from(files).map(({name}) => name);

            setImportState({
                status: 'loading',
                eventSource: 'Drag and Drop File',
                files,
                fileNames,
            });

            onSidebarChange({type: 'savedItems'});
        },
        [onSidebarChange, setImportState]
    );

    return (
        <div
            className={classes.importSurface}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            {children}
        </div>
    );
}
